import React from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import { faXmark } from '@fortawesome/pro-light-svg-icons/faXmark';
import { TypedMemo } from '../../../common/types';
import { LoadingIcon } from '../../../common/views/LoadingIcon';
import { Music24FullLogo } from '../../../music24/components/Music24FullLogo';
import { LanguagePicker } from '../../../app/locale/LanguagePicker';
import { addSongFlowMessages } from './addSongFlowMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole page; each part uses its own slice of it */
import styles from './CatalogAddSong.module.scss';
/* eslint-enable css-modules/no-unused-class */

export type SaveState = 'notSaved' | 'saving' | 'saved';

interface AddSongTopBarProps {
  isOnboarding: boolean;
  saveState: SaveState;
  stepper: React.ReactNode;
  onClose: () => void;
}

/**
 * The top bar (design catalog-add-song a1): the brand, the crumb, the stepper, the save state and
 * Close. "Add my song" of the onboarding also has the language picker (QA 2026-10-28, lit-1).
 */
const AddSongTopBarComponent: React.FC<AddSongTopBarProps> = ({ isOnboarding, saveState, stepper, onClose }) => {
  const intl = useIntl();
  const saveText = {
    notSaved: intl.formatMessage(addSongFlowMessages.notSaved),
    saving: intl.formatMessage(addSongFlowMessages.saving),
    saved: intl.formatMessage(addSongFlowMessages.saved),
  }[saveState];

  return (
    <header className={styles.top}>
      <div className={styles.brand}>
        <Music24FullLogo size={22} />
      </div>
      <span className={styles.crumb}>
        {intl.formatMessage(isOnboarding ? addSongFlowMessages.crumbOnboarding : addSongFlowMessages.crumb)}
      </span>
      <div className={styles.grow} />
      {stepper}
      <div className={styles.grow} />
      {isOnboarding && <LanguagePicker mode="account" />}
      <span className={styles.saved} role="status" data-testid="add-song-save-state">
        {saveState === 'saving' && <LoadingIcon small />}
        {saveState === 'saved' && <FontAwesomeIcon icon={faCheck} className={styles.savedIcon} />}
        <span className={styles.savedText}>{saveText}</span>
      </span>
      <button
        type="button"
        className={styles.close}
        onClick={onClose}
        aria-label={intl.formatMessage(addSongFlowMessages.close)}
        data-testid="add-song-close"
      >
        <FontAwesomeIcon icon={faXmark} />
      </button>
    </header>
  );
};

export const AddSongTopBar = TypedMemo(AddSongTopBarComponent);
